import { useRef, useEffect, useCallback } from "react";
import { X, Download, Share2 } from "lucide-react";
import { toast } from "sonner";

const W = 1080;
const H = 1080;
const GOLD = "#a855f7";
const GREEN = "#22c55e";

export function ShareFlexCard({ record, fishStreak, onClose }) {
  const canvasRef = useRef(null);
  const wins = record?.wins || 0;
  const losses = record?.losses || 0;
  const units = record?.units ?? 0;
  const winPct = wins + losses > 0 ? Math.round((wins / (wins + losses)) * 100) : 0;
  const streak = fishStreak?.streak || 0;

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    await Promise.all([
      document.fonts.load("700 120px Caveat"),
      document.fonts.load("900 52px 'Barlow Condensed'"),
      document.fonts.load("700 84px 'JetBrains Mono'"),
    ]).catch(() => {});
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, W, H);
    const glow = ctx.createRadialGradient(W / 2, 360, 80, W / 2, 360, 780);
    glow.addColorStop(0, "rgba(168,85,247,0.16)");
    glow.addColorStop(1, "rgba(168,85,247,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 10;
    ctx.strokeRect(28, 28, W - 56, H - 56);
    ctx.strokeStyle = "rgba(168,85,247,0.35)";
    ctx.lineWidth = 2;
    ctx.setLineDash([10, 8]);
    ctx.strokeRect(52, 52, W - 104, H - 104);
    ctx.setLineDash([]);
    ctx.textAlign = "center";

    ctx.fillStyle = GOLD;
    ctx.font = "700 92px Caveat";
    ctx.fillText("The Pooh Sheet", W / 2, 180);

    ctx.fillStyle = "#c9c9c9";
    ctx.font = "900 46px 'Barlow Condensed'";
    ctx.fillText("OFFICIAL SHEET RECORD", W / 2, 258);

    ctx.fillStyle = "#ffffff";
    ctx.font = "700 190px 'JetBrains Mono'";
    ctx.fillText(`${wins}-${losses}`, W / 2, 460);

    ctx.fillStyle = units >= 0 ? GREEN : "#f87171";
    ctx.font = "700 84px 'JetBrains Mono'";
    ctx.fillText(`${units >= 0 ? "+" : ""}${units}u`, W / 2 - 210, 590);
    ctx.fillStyle = GOLD;
    ctx.fillText(`${winPct}%`, W / 2 + 210, 590);

    ctx.fillStyle = "#8a8a8a";
    ctx.font = "600 34px 'Barlow Condensed'";
    ctx.fillText("PROFIT", W / 2 - 210, 640);
    ctx.fillText("WIN RATE", W / 2 + 210, 640);

    if (streak >= 2) {
      const bw = 620, bh = 84, bx = (W - bw) / 2, by = 690;
      ctx.strokeStyle = "#38bdf8";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.roundRect(bx, by, bw, bh, 42);
      ctx.stroke();
      ctx.fillStyle = "rgba(56,189,248,0.12)";
      ctx.beginPath();
      ctx.roundRect(bx, by, bw, bh, 42);
      ctx.fill();
      ctx.fillStyle = "#38bdf8";
      ctx.font = "900 44px 'Barlow Condensed'";
      ctx.fillText(`FISH CASHED ${streak} DAYS STRAIGHT`, W / 2, by + 56);
    }

    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(200, 830);
    ctx.lineTo(W - 200, 830);
    ctx.stroke();

    ctx.fillStyle = GOLD;
    ctx.font = "700 68px Caveat";
    ctx.fillText("It's Not A Sin, If You WIN!", W / 2, 925);

    ctx.fillStyle = "#9a9a9a";
    ctx.font = "500 34px 'JetBrains Mono'";
    ctx.fillText("thepoohsheet.com", W / 2, 990);
  }, [wins, losses, units, winPct, streak]);

  useEffect(() => {
    draw();
  }, [draw]);

  const download = () => {
    const a = document.createElement("a");
    a.download = "pooh-sheet-record.png";
    a.href = canvasRef.current.toDataURL("image/png");
    a.click();
    toast.success("Record card downloaded. Flex it!");
  };

  const share = async () => {
    canvasRef.current.toBlob(async (blob) => {
      const file = new File([blob], "pooh-sheet-record.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "The Pooh Sheet Record", text: `The Sheet is ${wins}-${losses} (${units >= 0 ? "+" : ""}${units}u). It's Not A Sin, If You WIN! thepoohsheet.com` });
        } catch (e) { /* cancelled */ }
      } else {
        download();
      }
    }, "image/png");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="share-flex-modal">
      <div className="card-gold border border-[#a855f7]/40 rounded-xl p-4 md:p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-bold uppercase">Flex the <span className="gold-text">Record</span></h3>
          <button onClick={onClose} data-testid="share-flex-close" className="text-neutral-500 hover:text-white p-1 transition-colors duration-200"><X size={18} /></button>
        </div>
        <canvas ref={canvasRef} width={W} height={H} className="w-full rounded-lg border border-white/10" />
        <div className="flex gap-2 mt-4">
          <button onClick={share} data-testid="share-flex-share-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-black font-bold uppercase tracking-wider text-xs py-3 rounded transition-colors duration-200">
            <Share2 size={14} /> Share
          </button>
          <button onClick={download} data-testid="share-flex-download-btn"
            className="flex-1 flex items-center justify-center gap-2 border border-white/20 hover:border-[#a855f7] text-white font-bold uppercase tracking-wider text-xs py-3 rounded transition-colors duration-200">
            <Download size={14} /> Download
          </button>
        </div>
      </div>
    </div>
  );
}
